import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOSchema from '../../components/SEOSchema';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [selectedUseCase, setSelectedUseCase] = useState('lead-followup');
  const [leadsPerMonth, setLeadsPerMonth] = useState(100);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleMobileMenuClick = (href: string) => {
    setMobileMenuOpen(false);
    // Smooth scroll to section
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 500) {
      setCharCount(text.length);
    } else {
      e.target.value = text.slice(0, 500);
      setCharCount(500);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate required fields
    const requiredFields = ['name', 'company', 'email', 'phone', 'budget', 'message'];
    for (const field of requiredFields) {
      if (!formData.get(field)) {
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
    }

    // Check textarea length
    const message = formData.get('message') as string;
    if (message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    // Collect selected services
    const services = formData.getAll('services');
    if (services.length === 0) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare data in application/x-www-form-urlencoded format
      const urlEncodedData = new URLSearchParams();
      
      urlEncodedData.append('name', formData.get('name') as string);
      urlEncodedData.append('company', formData.get('company') as string);
      urlEncodedData.append('email', formData.get('email') as string);
      urlEncodedData.append('phone', formData.get('phone') as string);
      urlEncodedData.append('budget', formData.get('budget') as string);
      urlEncodedData.append('services', services.join(', '));
      urlEncodedData.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d4o3d172prpfnnorm73g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
        
        // Reset status after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const useCases = {
    'lead-followup': {
      name: 'Lead Follow-ups',
      timePerLead: 15,
      description: 'Automatische Nachverfolgung von Leads'
    },
    'appointment-booking': {
      name: 'Terminbuchungen',
      timePerLead: 10,
      description: 'Automatisierte Terminvereinbarung'
    },
    'customer-support': {
      name: 'Kundenservice',
      timePerLead: 20,
      description: 'Beantwortung von Kundenanfragen'
    },
    'lead-qualification': {
      name: 'Lead-Qualifizierung',
      timePerLead: 12,
      description: 'Automatische Lead-Bewertung'
    },
    'database-reactivation': {
      name: 'Datenbank-Reaktivierung',
      timePerLead: 18,
      description: 'Reaktivierung inaktiver Leads'
    }
  };

  const currentUseCase = useCases[selectedUseCase as keyof typeof useCases];
  const hoursPerMonth = (leadsPerMonth * currentUseCase.timePerLead) / 60;
  const hoursPerYear = hoursPerMonth * 12;
  const costSavingsPerYear = hoursPerYear * 50; // Assuming €50/hour

  const faqData = [
    {
      question: 'Was macht KI Consults als AI Assisted Agency besonders?',
      answer: 'Als spezialisierte AI Assisted Agency kombinieren wir modernste KI-Technologie mit persönlicher Beratung und strategischer Expertise. Wir entwickeln maßgeschneiderte Lösungen und begleiten Sie von der Analyse bis zur kontinuierlichen Optimierung – mit einem dedizierten Team, das Ihre Geschäftsziele versteht.'
    },
    {
      question: 'Was ist der Unterschied zwischen Aktivierung und Reaktivierung?',
      answer: 'Aktivierung bedeutet, dass wir mit neuen eingehenden Leads arbeiten und diese sofort bearbeiten. Reaktivierung bezieht sich auf Leads, die bereits in Ihrer Pipeline sitzen und wieder angesprochen werden müssen. Als Agentur decken wir beide Bereiche ab.'
    },
    {
      question: 'Wie schnell kann Ihre Agentur das System implementieren?',
      answer: 'Die Implementierung dauert in der Regel 2-4 Wochen, abhängig von der Komplexität Ihrer bestehenden Systeme. Unser Agentur-Team sorgt für eine reibungslose Integration ohne Unterbrechung Ihrer laufenden Prozesse.'
    },
    {
      question: 'Welche Kanäle werden von Ihrer Agentur unterstützt?',
      answer: 'Wir unterstützen alle gängigen Kommunikationskanäle: E-Mail, SMS, WhatsApp, Facebook Messenger, Instagram DMs, Telefon und Website-Chat. Als flexible Agentur können wir auch weitere Kanäle nach Bedarf integrieren.'
    },
    {
      question: 'Benötige ich technische Kenntnisse?',
      answer: 'Nein, überhaupt nicht. Als Full-Service-Agentur übernehmen wir die gesamte technische Implementierung und Wartung. Sie erhalten ein benutzerfreundliches Dashboard zur Überwachung und können jederzeit Anpassungen bei uns anfordern.'
    },
    {
      question: 'Wie wird die Datenqualität durch Ihre Agentur sichergestellt?',
      answer: 'Unsere KI-Systeme verwenden fortschrittliche Algorithmen zur Lead-Qualifizierung und -Segmentierung. Als deutsche Agentur verarbeiten und speichern wir alle Daten nach strengsten DSGVO-Standards.'
    },
    {
      question: 'Was kostet die Zusammenarbeit mit Ihrer Agentur?',
      answer: 'Die Kosten variieren je nach Umfang und Anforderungen. Kontaktieren Sie uns für eine kostenlose Beratung und ein individuelles Agentur-Angebot, das auf Ihre spezifischen Bedürfnisse zugeschnitten ist.'
    }
  ];

  const professionalServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'KI Consults',
    description: 'KI-gestützte Lead-Aktivierung und Automatisierung für Unternehmen',
    url: 'https://example.com',
    areaServed: 'DE',
    serviceType: ['Lead-Aktivierung', 'KI-Automatisierung', 'Datenbank-Reaktivierung', 'Kundenservice-Automatisierung'],
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Am Backofenberg 45',
      addressLocality: 'Leegebruch',
      postalCode: '16767',
      addressCountry: 'DE'
    },
    telephone: '+4915904113381',
    email: 'info@kiconsults.de'
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <SEOSchema data={[professionalServiceSchema, faqSchema]} />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
        <div
          className={`max-w-7xl mx-auto rounded-2xl transition-all duration-300 ${
            scrolled 
              ? 'bg-white/5 backdrop-blur-2xl shadow-xl border border-white/30' 
              : 'bg-white/15 backdrop-blur-xl border border-white/20'
          }`}
        >
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-robot-2-line text-white text-xl"></i>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">
                  KI Consults
                </span>
              </Link>

              <div className="hidden md:flex items-center space-x-8">
                <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  Über uns
                </a>
                <a href="#services" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  Leistungen
                </a>
                <a href="#benefits" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  Vorteile
                </a>
                <a href="#process" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  Prozess
                </a>
                <a href="#faq" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                  FAQ
                </a>
                <a
                  href="#contact"
                  className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Kontakt
                </a>
              </div>

              <button 
                className="md:hidden text-gray-700 cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            <div
              className={`md:hidden overflow-hidden transition-all duration-300 ${
                mobileMenuOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="flex flex-col space-y-4 py-4 bg-white/80 backdrop-blur-lg rounded-xl shadow-lg border border-white/40">
                <a
                  href="#about"
                  onClick={() => handleMobileMenuClick('#about')}
                  className="px-6 py-2 text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
                >
                  Über uns
                </a>
                <a
                  href="#services"
                  onClick={() => handleMobileMenuClick('#services')}
                  className="px-6 py-2 text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
                >
                  Leistungen
                </a>
                <a
                  href="#benefits"
                  onClick={() => handleMobileMenuClick('#benefits')}
                  className="px-6 py-2 text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
                >
                  Vorteile
                </a>
                <a
                  href="#process"
                  onClick={() => handleMobileMenuClick('#process')}
                  className="px-6 py-2 text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
                >
                  Prozess
                </a>
                <a
                  href="#faq"
                  onClick={() => handleMobileMenuClick('#faq')}
                  className="px-6 py-2 text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
                >
                  FAQ
                </a>
                <a
                  href="#contact"
                  onClick={() => handleMobileMenuClick('#contact')}
                  className="mx-6 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 text-center cursor-pointer"
                >
                  Kontakt
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-transparent to-blue-50/50"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-8 animate-fade-in">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span className="text-sm text-teal-700 font-medium">Ihre AI Assisted Agency</span>
            </div>

            <h1 className="text-4xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight animate-slide-up">
              Verwandeln Sie mehr Leads in zahlende Kunden{' '}
              <span className="bg-gradient-to-r from-teal-500 to-teal-600 bg-clip-text text-transparent">
                mit KI-gestützter Automatisierung
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-10 leading-relaxed animate-slide-up delay-200">
              Sparen Sie sich stundenlange manuelle Antworten auf Leads. 
              Lassen Sie die KI Buchungen, <span className="whitespace-nowrap">Follow-ups</span> und Qualifizierung 24/7 übernehmen.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-slide-up delay-300">
              <a
                href="#contact"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>Kostenlose Beratung buchen</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </a>
              <a
                href="#about"
                className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold border-2 border-gray-200 hover:border-teal-500 hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Mehr über uns
              </a>
            </div>

            <div className="flex items-center justify-center space-x-8 text-sm text-gray-600 animate-fade-in delay-500">
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-500 text-lg"></i>
                <span>Persönliche Betreuung</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-teal-500 text-lg"></i>
                <span>Maßgeschneiderte Lösungen</span>
              </div>
            </div>
          </div>

          {/* ROI Calculator Section */}
          <div className="mt-16 animate-fade-in delay-700">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Berechnen Sie Ihre Zeitersparnis
              </h2>
              <p className="text-lg text-gray-600">
                Sehen Sie, wie viel Zeit und Kosten Sie mit KI-Automatisierung einsparen können
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              {/* Calculator Card */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100 mb-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Input Section */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Wählen Sie Ihren Anwendungsfall
                    </label>
                    <select
                      value={selectedUseCase}
                      onChange={(e) => setSelectedUseCase(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-gray-900 cursor-pointer"
                    >
                      {Object.entries(useCases).map(([key, useCase]) => (
                        <option key={key} value={key}>
                          {useCase.name} - {useCase.description}
                        </option>
                      ))}
                    </select>

                    <label className="block text-sm font-semibold text-gray-700 mb-3 mt-6">
                      Anzahl der Leads pro Monat: {leadsPerMonth}
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="1000"
                      step="10"
                      value={leadsPerMonth}
                      onChange={(e) => setLeadsPerMonth(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>10</span>
                      <span>500</span>
                      <span>1000</span>
                    </div>

                    <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-100">
                      <div className="text-sm text-gray-600 mb-1">Durchschnittliche Zeit pro Lead:</div>
                      <div className="text-2xl font-bold text-teal-600">
                        {currentUseCase.timePerLead} Minuten
                      </div>
                    </div>
                  </div>

                  {/* Results Section */}
                  <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-4 md:p-8 border border-teal-100 hover:shadow-xl transition-all duration-300">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">
                      Ihre Zeitersparnis
                    </h3>
                    
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-4">
                      <div className="bg-white rounded-xl p-3 md:p-4 border border-teal-100">
                        <div className="text-xs md:text-sm text-gray-600 mb-1">Pro Monat</div>
                        <div className="text-xl md:text-3xl font-bold text-teal-600">
                          {hoursPerMonth.toFixed(1)} h
                        </div>
                      </div>

                      <div className="bg-white rounded-xl p-3 md:p-4 border border-blue-100">
                        <div className="text-xs md:text-sm text-gray-600 mb-1">Pro Jahr</div>
                        <div className="text-xl md:text-3xl font-bold text-blue-600">
                          {hoursPerYear.toFixed(0)} h
                        </div>
                      </div>

                      <div className="bg-white rounded-xl p-3 md:p-4 border border-green-100 col-span-2 md:col-span-1">
                        <div className="text-xs md:text-sm text-gray-600 mb-1">Kostenersparnis pro Jahr</div>
                        <div className="text-xl md:text-3xl font-bold text-green-600">
                          €{costSavingsPerYear.toLocaleString('de-DE')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Basierend auf €50/Stunde
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pros vs Cons Comparison */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Advantages */}
                <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                      <i className="ri-check-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Mit KI-Automatisierung</h3>
                  </div>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">24/7 Verfügbarkeit ohne Pausen</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Sofortige Antwortzeiten</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Konsistente Qualität bei jeder Interaktion</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Unbegrenzte Skalierbarkeit</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Automatische Datenerfassung & Analyse</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Keine menschlichen Fehler</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Massive Kostenreduktion</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Multi-Channel-Integration</span>
                    </li>
                  </ul>
                </div>

                {/* Disadvantages */}
                <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 border border-red-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                      <i className="ri-close-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Ohne KI-Automatisierung</h3>
                  </div>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Begrenzte Arbeitszeiten</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Verzögerte Antworten außerhalb der Geschäftszeiten</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Inkonsistente Qualität durch menschliche Faktoren</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Hohe Personalkosten bei Skalierung</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Manuelle Dateneingabe anfällig für Fehler</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Menschliche Fehler und Vergesslichkeit</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Steigende Kosten mit Wachstum</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-close-line text-red-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Komplexe Multi-Channel-Verwaltung</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Agency Section */}
      <section id="about" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-200/30 to-blue-200/30 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <img
                  src="https://public.readdy.ai/ai/img_res/611c8fb07d962c1d48fab2ef785c7350.jpg"
                  alt="KI Consults Team"
                  className="w-full h-auto rounded-xl object-cover"
                />
              </div>
            </div>

            <div>
              <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
                <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
                <span>Über KI Consults</span>
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Ihre AI Assisted Agency für digitale Transformation
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Als spezialisierte AI Assisted Agency verbinden wir modernste KI-Technologie mit strategischer Unternehmensberatung. 
                Unser Fokus liegt auf der intelligenten Aktivierung und Optimierung Ihrer Lead-Pipeline.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Wir verstehen, dass jedes Unternehmen einzigartig ist. Deshalb entwickeln wir maßgeschneiderte KI-Lösungen, 
                die perfekt auf Ihre Geschäftsziele und Prozesse abgestimmt sind. Von der Strategie bis zur Implementierung – 
                wir begleiten Sie auf jedem Schritt Ihrer digitalen Transformation.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-teal-50 to-white p-6 rounded-xl border border-teal-100">
                  <div className="text-3xl font-bold text-teal-600 mb-2">100+</div>
                  <div className="text-sm text-gray-600">Erfolgreiche Projekte</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                  <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                  <div className="text-sm text-gray-600">KI-Support</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                  <div className="text-3xl font-bold text-purple-600 mb-2">95%</div>
                  <div className="text-sm text-gray-600">Kundenzufriedenheit</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-xl border border-green-100">
                  <div className="text-3xl font-bold text-green-600 mb-2">3x</div>
                  <div className="text-sm text-gray-600">ROI-Steigerung</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span>Unsere Leistungen</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Umfassende KI-Lösungen für Ihr Unternehmen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Als AI Assisted Agency bieten wir ein vollständiges Spektrum an KI-gestützten Services, 
              die jeden Aspekt Ihrer Lead-Pipeline optimieren.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-database-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Datenbank-Reaktivierung</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Wir reaktivieren inaktive Leads in Ihrem CRM oder ERP und extrahieren Wert aus bereits getätigten Investitionen.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Automatische Lead-Nachverfolgung</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Intelligente Segmentierung</span>
                </li>
              </ul>
            </div>

            {/* Service Card 2 */}
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-user-follow-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proaktives Engagement</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Unsere Agentur erreicht Interessenten automatisch nach Formularausfüllung, Käufen oder Terminbuchungen.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Formular-Follow-ups</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Termin-Erinnerungen</span>
                </li>
              </ul>
            </div>

            {/* Service Card 3 */}
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-message-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Eingehende DMs</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Wir verarbeiten eingehende Direktnachrichten auf allen Kanälen automatisch mit sofortigen Antworten.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Multi-Channel-Support</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>24/7 Verfügbarkeit</span>
                </li>
              </ul>
            </div>

            {/* Service Card 4 */}
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Anrufbearbeitung</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Unsere KI-gestützte Anrufbeantwortung sorgt dafür, dass Sie nie wieder einen wichtigen Anruf verpassen.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Intelligente Anrufweiterleitung</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Automatische Transkription</span>
                </li>
              </ul>
            </div>

            {/* Service Card 5 */}
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-chat-smile-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Website-Chat-Widget</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Wir implementieren intelligente Chat-Widgets, die Website-Besucher automatisch in qualifizierte Leads konvertieren.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Lead-Qualifizierung</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Sofortige Antworten</span>
                </li>
              </ul>
            </div>

            {/* Service Card 6 */}
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-share-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Empfehlungen & Affiliates</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Wir entwickeln automatisierte Kampagnen für Empfehlungen und Affiliates zur Netzwerkerweiterung.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Automatische Kampagnen</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-600">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Tracking & Belohnungen</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span>Ihre Vorteile</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Warum KI Consults als Ihre AI Assisted Agency?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Als spezialisierte Agentur decken wir sowohl eingehende als auch proaktive Engagement-Strategien ab, 
              um Ihr Geschäftswachstum nachhaltig zu maximieren.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-up-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Eingehender Traffic</h3>
                  <p className="text-gray-600">
                    Unsere Agentur verarbeitet eingehende DMs, beantwortet Anrufe und konvertiert Website-Besucher automatisch in qualifizierte Leads.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-rocket-line text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Proaktives Engagement</h3>
                  <p className="text-gray-600">
                    Wir erreichen Leads nach Formularausfüllung, Käufen oder Terminbuchungen mit automatischer Nachverfolgung.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-refresh-line text-purple-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Lead-Reaktivierung</h3>
                  <p className="text-gray-600">
                    Wir extrahieren Wert aus inaktiven Leads in Ihrer Datenbank und holen mehr aus Ihren bestehenden Investitionen heraus.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-team-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Empfehlungsprogramm</h3>
                  <p className="text-gray-600">
                    Automatisierte Kampagnen für Empfehlungen und Affiliates erweitern Ihr Netzwerk ohne zusätzlichen Aufwand.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-200/30 to-blue-200/30 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20AI-powered%20business%20automation%20dashboard%20showing%20lead%20conversion%20funnel%20with%20automated%20messaging%20workflows%2C%20intelligent%20chatbot%20interactions%2C%20and%20real-time%20analytics%20graphs%20on%20sleek%20digital%20interface%2C%20professional%20business%20technology%20illustration%20with%20teal%20and%20blue%20gradient%20accents%2C%20clean%20minimalist%20design%20showing%20customer%20journey%20automation%20and%20AI%20assistant%20processing%20multiple%20communication%20channels%20simultaneously%2C%20futuristic%20yet%20approachable%20aesthetic%20with%20data%20visualization%20and%20conversion%20metrics&width=600&height=600&seq=benefits-illustration&orientation=squarish"
                  alt="Benefits Illustration"
                  className="w-full h-auto rounded-xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span>Für wen wir arbeiten</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Unsere idealen Kunden
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Als AI Assisted Agency unterstützen wir Unternehmen, die mit digitalen KI-Lösungen skalieren möchten
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border border-teal-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-building-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Skalierungsorientierte Unternehmen</h3>
              <p className="text-gray-600">
                Unternehmen, die ihr Geschäft mit digitalen KI-Lösungen skalieren möchten
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">KI-interessierte Unternehmer</h3>
              <p className="text-gray-600">
                Geschäftsinhaber, die an KI-gesteuerter Kundenakquise interessiert sind
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovative Unternehmer</h3>
              <p className="text-gray-600">
                Unternehmer, die einen KI-Agenten in ihrem Geschäft einführen möchten
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-store-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kleine Unternehmen</h3>
              <p className="text-gray-600">
                Kleinunternehmer, die vorhersehbare Online-Kundengewinnung mit KI suchen
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-group-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kundenorientierte Branchen</h3>
              <p className="text-gray-600">
                Fachleute in Branchen mit vielen Kunden als Zielgruppe
              </p>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-white rounded-2xl p-8 border border-pink-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-magic-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Effizienz-Suchende</h3>
              <p className="text-gray-600">
                Unternehmen, die mühelose und effiziente Prozesse mit KI implementieren möchten
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span>Unser Agentur-Prozess</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Wie wir arbeiten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ein bewährter Agentur-Prozess zur Implementierung Ihres KI-Lead-Aktivierungssystems
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-teal-600/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-teal-500 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 text-white text-2xl font-bold flex-shrink-0">
                  01
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Analyse & Beratung</h3>
                <p className="text-gray-600 flex-grow">
                  Wir analysieren Ihre aktuelle Lead-Pipeline und beraten Sie zu den besten KI-Lösungen für Ihr Unternehmen
                </p>
              </div>
            </div>

            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 text-white text-2xl font-bold flex-shrink-0">
                  02
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Strategie-Entwicklung</h3>
                <p className="text-gray-600 flex-grow">
                  Unsere Agentur entwickelt eine maßgeschneiderte KI-Strategie für Ihr Unternehmen
                </p>
              </div>
            </div>

            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-purple-500 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 text-white text-2xl font-bold flex-shrink-0">
                  03
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Implementierung</h3>
                <p className="text-gray-600 flex-grow">
                  Wir integrieren das KI-System schnell und nahtlos in Ihre bestehenden Prozesse
                </p>
              </div>
            </div>

            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-green-500 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 text-white text-2xl font-bold flex-shrink-0">
                  04
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Betreuung & Optimierung</h3>
                <p className="text-gray-600 flex-grow">
                  Kontinuierliche Betreuung und Optimierung durch unsere Agentur für maximale Ergebnisse
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span>FAQ</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Häufig gestellte Fragen
            </h2>
            <p className="text-xl text-gray-600">
              Alles, was Sie über unsere AI Assisted Agency und unsere KI-Lösungen wissen müssen
            </p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 transition-all duration-300"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-teal-500 transition-transform duration-300 flex-shrink-0 ${
                      activeAccordion === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    activeAccordion === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-semibold mb-4">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <span>Kontakt / Anfrage</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Lassen Sie uns über Ihr Projekt sprechen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir erstellen Ihnen gerne ein unverbindliches Angebot.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Strategiegespräch anfragen</h3>
              
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleFormSubmit}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-teal-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Firma <span className="text-teal-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail <span className="text-teal-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon <span className="text-teal-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durchschnittliches monatliches Budget für KI <span className="text-teal-500">*</span>
                  </label>
                  <select
                    name="budget"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all cursor-pointer"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="0-500">0 - 500 €</option>
                    <option value="501-1000">501 - 1000 €</option>
                    <option value="1001-1500">1001 - 1500 €</option>
                    <option value="1501-2000">1501 - 2000 €</option>
                    <option value="2001-5000">2001 - 5000 €</option>
                    <option value="5000+">5000+ €</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gewünschter Leistungsumfang <span className="text-teal-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" name="services" value="lead-aktivierung" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-500 cursor-pointer" />
                      <span className="text-gray-700">Lead-Aktivierung</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" name="services" value="datenbank-reaktivierung" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-500 cursor-pointer" />
                      <span className="text-gray-700">Datenbank-Reaktivierung</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" name="services" value="anrufbearbeitung" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-500 cursor-pointer" />
                      <span className="text-gray-700">Anrufbearbeitung</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" name="services" value="chat-widget" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-500 cursor-pointer" />
                      <span className="text-gray-700">Website-Chat-Widget</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" name="services" value="ki-beratung" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-500 cursor-pointer" />
                      <span className="text-gray-700">KI-Beratung</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Projektkurzbeschreibung <span className="text-teal-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Beschreiben Sie kurz Ihr Bauvorhaben, besondere Anforderungen und den gewünschten Zeitrahmen..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all resize-none"
                    onChange={handleTextareaChange}
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">{charCount}/500 Zeichen</div>
                </div>

                <div className="text-xs text-gray-500">
                  <span className="text-teal-500">*</span> Pflichtfelder. Ihre Daten werden vertraulich behandelt und nur zur Bearbeitung Ihrer Anfrage verwendet.
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
                    <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-green-800 font-semibold">Vielen Dank für Ihre Anfrage!</p>
                      <p className="text-green-700 text-sm mt-1">Wir werden uns schnellstmöglich bei Ihnen melden.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                    <i className="ri-error-warning-line text-red-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-red-800 font-semibold">Fehler beim Senden</p>
                      <p className="text-red-700 text-sm mt-1">Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                      <span>Wird gesendet...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-xl"></i>
                      <span>Rückruf vereinbaren</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Direkter Kontakt</h3>
              
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Telefon</h4>
                    <a href="tel:+4915904113381" className="text-gray-600 hover:text-teal-600 transition-colors">
                      +49 159 04113381
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Mo-Fr 8:00-17:00 Uhr</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">E-Mail</h4>
                    <a href="mailto:info@kiconsults.de" className="text-gray-600 hover:text-teal-600 transition-colors">
                      info@kiconsults.de
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Antwort innerhalb von 24h</p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Standort</h4>
                    <p className="text-gray-600">Am Backofenberg 45, 16767 Leegebruch</p>
                    <p className="text-sm text-gray-500 mt-1">Bundesweite Projektbetreuung</p>
                  </div>
                </div>

                {/* Quick Consultation */}
                <div className="mt-8 p-6 bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl border border-teal-100">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Schnelle Erstberatung</h4>
                  <p className="text-gray-600 mb-4 text-sm">
                    Nutzen Sie unseren KI-Assistenten für eine erste Einschätzung Ihres Projekts oder vereinbaren Sie direkt einen Beratungstermin.
                  </p>
                  <button
                    onClick={() => {
                      const vapiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                      if (vapiButton) vapiButton.click();
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-message-3-line text-lg"></i>
                    <span>Beratung starten</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8 text-center md:text-left">
            <div>
              <div className="flex items-center space-x-2 mb-4 justify-center md:justify-start">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-robot-2-line text-white text-lg"></i>
                </div>
                <span className="text-xl font-bold text-white">KI Consults</span>
              </div>
              <p className="text-sm text-gray-400 mb-2">
                Ihre AI Assisted Agency für KI-gestützte Lead-Aktivierung und Geschäftswachstum.
              </p>
              <p className="text-xs text-gray-500">
                Spezialisierte Agentur für digitale Transformation
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Agentur-Leistungen</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Datenbank-Reaktivierung</a></li>
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Proaktives Engagement</a></li>
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Eingehende DMs</a></li>
                <li><a href="#services" className="hover:text-teal-500 transition-colors cursor-pointer">Anrufbearbeitung</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Agentur</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="hover:text-teal-500 transition-colors cursor-pointer">Über uns</a></li>
                <li><a href="#benefits" className="hover:text-teal-500 transition-colors cursor-pointer">Vorteile</a></li>
                <li><a href="#process" className="hover:text-teal-500 transition-colors cursor-pointer">Prozess</a></li>
                <li><a href="#faq" className="hover:text-teal-500 transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="#contact" className="hover:text-teal-500 transition-colors cursor-pointer">Kontakt</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Kontakt</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center space-x-2 justify-center md:justify-start">
                  <i className="ri-mail-line text-teal-400"></i>
                  <a href="mailto:info@kiconsults.de" className="hover:text-teal-400 transition-colors">info@kiconsults.de</a>
                </li>
                <li className="flex items-center space-x-2 justify-center md:justify-start">
                  <i className="ri-global-line text-teal-400"></i>
                  <span>kiconsults.de</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.linkedin.com/in/wieseandreas/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-teal-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-gray-300 hover:text-white text-xl"></i>
                </a>
              </div>
              <p className="text-sm text-gray-400 text-center">
                © 2025 KI Consults - AI Assisted Agency. Alle Rechte vorbehalten.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-slide-up {
          animation: slide-up 0.8s ease-out forwards;
        }

        .delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }

        .delay-300 {
          animation-delay: 0.3s;
          opacity: 0;
        }

        .delay-500 {
          animation-delay: 0.5s;
          opacity: 0;
        }

        .delay-700 {
          animation-delay: 0.7s;
          opacity: 0;
        }

        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}
